import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { GlassCard, Button, Icon, StatusPill, ProgressBar, Tabs, Modal, ModalClose, Pagination } from '../../campaign-os';
import { usePageChrome } from '../PageChrome';
import { useApiData } from '../data/useApiData';
import { useShellToast } from '../useShellToast';
import { useAuth } from '../../../../contexts/AuthContext';
import { selfRegularizationService, getErrorMessage } from '../../../../services/apiService';
import { checkByKey, RUN_STATUS_PILL, vendorNeedsAttention, isApprovable, formatInr } from '../data/selfRegularization';
import { formatDateTime } from '../data/format';
import { RegistryNotice, CheckChip, CheckStatusPill, OutcomePill, Stat } from '../components/SelfRegularizationBits';

/** Vendors on one page of the run. A run can hold a thousand; the tabs and counts still cover all of them. */
const VENDORS_PER_PAGE = 25;

/**
 * One verification run: progress while it verifies, then what each registry said about each
 * vendor — field by field, in words — and the admin's approval that writes it to the record.
 *
 * A vendor can be excluded before approving; its facts stay on the run for the record but
 * nothing is written for it. A fact marked "Check" is never written automatically, approved or not.
 */

const num = (n) => Number(n || 0).toLocaleString('en-IN');
const LIVE = new Set(['running', 'queued']);
const POLL_MS = 1200;
/** Acknowledgements arrive over hours and days; a slow poll is plenty. */
const ACK_POLL_MS = 20_000;

const VENDOR_PILL = {
  queued: { pill: 'pending', label: 'Queued' },
  verifying: { pill: 'running', label: 'Verifying' },
  done: { pill: 'approved', label: 'Verified' },
  skipped: { pill: 'draft', label: 'Skipped' },
  failed: { pill: 'failed', label: 'Failed' },
};

function bucketOf(v) {
  if (v.status === 'queued' || v.status === 'verifying') return 'pending';
  if (v.status === 'skipped' || v.status === 'failed') return 'other';
  return vendorNeedsAttention(v) ? 'attention' : 'clean';
}

/** The facts table: field · on record · from registry · outcome. */
function FactsTable({ facts }) {
  return (
    <div className="overflow-x-auto rounded-xl border border-cos-line-subtle bg-white/50">
      <table className="w-full text-[13px]">
        <thead>
          <tr className="border-b border-cos-line-subtle">
            {['Field', 'On record', 'From registry', 'Outcome'].map((h) => (
              <th key={h} className="whitespace-nowrap px-3 py-2 text-left text-2xs font-bold uppercase tracking-[0.08em] text-cos-ink-muted">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {facts.map((f) => {
            const quiet = f.outcome === 'match';
            return (
              <React.Fragment key={f.field}>
                <tr className={`border-b border-cos-line-subtle last:border-0 ${quiet ? '' : 'bg-white/60'}`}>
                  <td className={`px-3 py-2 ${quiet ? 'text-cos-ink-secondary' : 'font-semibold text-cos-ink'}`}>{f.label}</td>
                  <td className={`px-3 py-2 ${quiet ? 'text-cos-ink-secondary' : f.outcome === 'update' || f.outcome === 'check' ? 'text-cos-ink line-through decoration-cos-ink-muted/60' : 'text-cos-ink-muted'}`}>
                    {f.current ?? <span className="italic text-cos-ink-muted">empty</span>}
                  </td>
                  <td className={`px-3 py-2 ${quiet ? 'text-cos-ink-secondary' : 'font-semibold text-cos-ink'}`}>
                    {f.verified ?? <span className="italic text-cos-ink-muted">not returned</span>}
                  </td>
                  <td className="px-3 py-2">
                    <OutcomePill outcome={f.outcome} />
                  </td>
                </tr>
                {f.note && (
                  <tr className="border-b border-cos-line-subtle last:border-0">
                    <td colSpan={4} className="px-3 pb-2.5 pt-0">
                      <p className="flex items-start gap-1.5 rounded-lg bg-cos-status-coral-bg px-2.5 py-1.5 text-[12px] leading-[17px] text-cos-status-coral">
                        <Icon name="priority_high" className="mt-px shrink-0 text-[14px]" weight={600} />
                        {f.note}
                      </p>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function CheckResult({ check }) {
  const meta = checkByKey(check.key);
  return (
    <div className="rounded-xl border border-cos-line bg-white/40 p-3.5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="grid h-8 w-8 place-items-center rounded-lg bg-cos-teal-faint text-cos-teal-deep">
            <Icon name={meta?.icon || 'fact_check'} className="text-[18px]" weight={500} />
          </span>
          <div>
            <div className="text-[13px] font-bold text-cos-ink">{meta?.label || check.key}</div>
            <div className="mono text-2xs text-cos-ink-muted">
              {check.calls ? `${check.calls} call · ${formatInr(check.cost_inr)}` : meta?.local ? 'from the GSTIN · no call · free' : 'no call made'}
            </div>
          </div>
        </div>
        <CheckStatusPill checkKey={check.key} status={check.status} withName={false} />
      </div>
      {check.reason && (
        <p className={`mt-2.5 flex items-start gap-1.5 text-[12px] leading-[17px] ${check.status === 'skipped' ? 'text-cos-ink-secondary' : 'text-cos-status-coral'}`}>
          <Icon name={check.status === 'skipped' ? 'info' : 'error'} className="mt-px shrink-0 text-[14px]" />
          {check.reason}
        </p>
      )}
      {check.facts?.length > 0 && (
        <div className="mt-3">
          <FactsTable facts={check.facts} />
        </div>
      )}
    </div>
  );
}

/** What approval did for a vendor: the fields written, the ones left alone because the record moved on, or a dry run. */
function AppliedSummary({ applied }) {
  if (!applied) return null;
  const { written = [], stale = [], stamped = [], dry_run: dryRun } = applied;
  return (
    <div className={`rounded-xl border px-3.5 py-3 text-[12px] leading-[18px] ${dryRun ? 'border-cos-beige-light bg-cos-beige-light/40' : 'border-cos-teal/25 bg-cos-teal-faint/40'}`}>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 font-semibold text-cos-ink">
        <span className="flex items-center gap-1">
          <Icon name={dryRun ? 'science' : 'task_alt'} className={`text-[16px] ${dryRun ? 'text-cos-beige-ink' : 'text-cos-teal-deep'}`} weight={500} />
          {dryRun ? 'Mock run — nothing was written' : written.length ? `${num(written.length)} field${written.length === 1 ? '' : 's'} written to the record` : 'Nothing needed writing'}
        </span>
        {stamped.length > 0 && <span className="text-cos-ink-secondary">Confirmed: {stamped.map((k) => checkByKey(k)?.label || k).join(', ')}</span>}
      </div>
      {written.length > 0 && (
        <ul className="mt-1.5 grid grid-cols-1 gap-x-4 gap-y-0.5 md:grid-cols-2">
          {written.map((w) => (
            <li key={w.field} className="flex min-w-0 items-baseline gap-1.5">
              <span className="shrink-0 text-cos-ink-secondary">{w.label}:</span>
              <span className="truncate font-medium text-cos-ink">{w.to ?? '—'}</span>
            </li>
          ))}
        </ul>
      )}
      {stale.length > 0 && (
        <p className="mt-1.5 flex items-start gap-1.5 text-cos-status-amber">
          <Icon name="history" className="mt-px shrink-0 text-[14px]" />
          <span>
            {stale.length === 1 ? 'One field was' : `${num(stale.length)} fields were`} left alone because the record changed after the run:{' '}
            {stale.map((s) => s.label).join(', ')}.
          </span>
        </p>
      )}
    </div>
  );
}

/** One vendor: a header row that opens into its checks. */
function VendorRow({ vendor, open, onToggle, canDecide, excluded, onExclude }) {
  const p = VENDOR_PILL[vendor.status] || { pill: 'draft', label: vendor.status };
  const attention = vendorNeedsAttention(vendor);
  const approval = vendor.approval;
  return (
    <div className={`rounded-2xl border transition-colors ${open ? 'border-cos-teal/40 bg-white/70' : 'border-cos-line bg-white/45 hover:bg-white/65'} ${excluded ? 'opacity-70' : ''}`}>
      <div className="flex items-center gap-3 px-4 py-3">
        <button type="button" onClick={onToggle} className="flex min-w-0 flex-1 items-center gap-3 text-left" aria-expanded={open}>
          <Icon name={open ? 'expand_more' : 'chevron_right'} className="shrink-0 text-[20px] text-cos-ink-muted" />
          <div className="min-w-0">
            <div className={`truncate font-semibold ${excluded ? 'text-cos-ink-secondary line-through' : 'text-cos-ink'}`}>{vendor.company_name}</div>
            <div className="mono text-2xs text-cos-ink-muted">{vendor.vendor_code || 'No vendor code'}</div>
          </div>
          <div className="ml-auto hidden flex-wrap items-center justify-end gap-1 md:flex">
            {vendor.status === 'done' || vendor.status === 'failed' ? (
              vendor.checks.map((c) => <CheckStatusPill key={c.key} checkKey={c.key} status={c.status} />)
            ) : (
              <StatusPill status={p.pill} label={p.label} />
            )}
          </div>
        </button>
        <div className="flex shrink-0 items-center gap-2">
          {approval === 'approved' && vendor.acknowledgement === 'acknowledged' && <StatusPill status="completed" label="Acknowledged" />}
          {approval === 'approved' && vendor.acknowledgement === 'disputed' && <StatusPill status="risk" label="Disputed" />}
          {approval === 'approved' && vendor.acknowledgement === 'requested' && <StatusPill status="pending" label="Awaiting acknowledgement" />}
          {approval === 'approved' && (vendor.acknowledgement || 'none') === 'none' && <StatusPill status="approved" label="Applied" />}
          {approval === 'excluded' && <StatusPill status="draft" label="Excluded" />}
          {approval === 'pending' && canDecide && isApprovable(vendor) && (
            <Button variant={excluded ? 'secondary' : 'ghost'} size="sm" icon={excluded ? 'undo' : 'block'} onClick={onExclude} title={excluded ? 'Include this vendor in the approval' : 'Leave this vendor out of the approval — nothing is written for it'}>
              {excluded ? 'Include' : 'Exclude'}
            </Button>
          )}
          {attention && approval === 'pending' && <Icon name="priority_high" className="text-[18px] text-cos-status-coral" weight={600} title="Needs attention" />}
        </div>
      </div>
      {open && (
        <div className="flex flex-col gap-2.5 border-t border-cos-line-subtle px-4 py-3.5">
          {approval === 'approved' && <AppliedSummary applied={vendor.applied} />}
          {vendor.acknowledgement === 'disputed' && vendor.acknowledgement_note && (
            <p className="flex items-start gap-1.5 rounded-xl border border-cos-status-coral/30 bg-cos-status-coral-bg px-3.5 py-2.5 text-[12px] leading-[18px] text-cos-ink">
              <Icon name="feedback" className="mt-px shrink-0 text-[15px] text-cos-status-coral" weight={500} />
              <span>
                <span className="font-semibold">{vendor.acknowledged_by_name || 'The vendor'} says something is wrong:</span> “{vendor.acknowledgement_note}”
              </span>
            </p>
          )}
          {vendor.acknowledgement === 'acknowledged' && (
            <p className="text-[12px] text-cos-ink-secondary">
              Confirmed by {vendor.acknowledged_by_name || 'the vendor'} on {formatDateTime(vendor.acknowledged_at)}.
            </p>
          )}
          {vendor.checks.map((c) => (
            <CheckResult key={c.key} check={c} />
          ))}
        </div>
      )}
    </div>
  );
}

export function SelfRegularizationRun() {
  const { runId } = useParams();
  const navigate = useNavigate();
  const toast = useShellToast();
  const { isAdmin } = useAuth();

  const api = useApiData(() => selfRegularizationService.getRun(runId), [runId], { cacheKey: `selfreg:run:${runId}` });
  const run = api.data?.data || null;
  const live = !!run && LIVE.has(run.status);

  // While the registries are answering, the page follows along; once settled it stops asking —
  // unless vendors have been asked to acknowledge, whose answers are worth watching for too.
  const awaitingAnswers = !!run && (run.counts?.ack_requested || 0) > 0;
  useEffect(() => {
    if (!live && !awaitingAnswers) return undefined;
    const t = setInterval(api.refetch, live ? POLL_MS : ACK_POLL_MS);
    return () => clearInterval(t);
  }, [live, awaitingAnswers, api.refetch]);

  const [tab, setTab] = useState('all');
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState(() => new Set());
  const [excluded, setExcluded] = useState(() => new Set());
  const [confirming, setConfirming] = useState(false);
  const [approving, setApproving] = useState(false);
  const [askingAck, setAskingAck] = useState(false);
  const [requestingAck, setRequestingAck] = useState(false);
  const [exporting, setExporting] = useState(false);

  const counts = run?.counts || { total: 0, done: 0, verified: 0, attention: 0, not_found: 0, skipped: 0, failed: 0, approved: 0, excluded: 0 };
  const awaiting = run?.status === 'awaiting_approval';
  const canDecide = isAdmin && awaiting;
  const approvable = useMemo(() => (run?.vendors || []).filter((v) => isApprovable(v) && v.approval === 'pending'), [run]);
  const toApprove = approvable.filter((v) => !excluded.has(v.vendor_id)).length;
  const settled = run?.status === 'approved' || run?.status === 'partially_approved';
  // Approved vendors not yet asked to confirm what was written.
  const ackable = useMemo(() => (run?.vendors || []).filter((v) => v.approval === 'approved' && (v.acknowledgement || 'none') === 'none').length, [run]);

  const buckets = useMemo(() => {
    const b = { all: [], attention: [], clean: [], other: [], pending: [] };
    for (const v of run?.vendors || []) {
      b.all.push(v);
      b[bucketOf(v)].push(v);
    }
    return b;
  }, [run]);
  const visible = buckets[tab] || buckets.all;
  const pageCount = Math.max(1, Math.ceil(visible.length / VENDORS_PER_PAGE));
  const safePage = Math.min(page, pageCount);
  const pageRows = visible.slice((safePage - 1) * VENDORS_PER_PAGE, safePage * VENDORS_PER_PAGE);

  const statusPill = RUN_STATUS_PILL[run?.status] || { pill: 'draft', label: run?.status || '' };

  usePageChrome(
    () => ({
      title: run ? run.name : 'Verification run',
      subtitle: run ? `Run #${run.id} · started ${formatDateTime(run.started_at || run.created_at)} by ${run.created_by?.full_name || 'Admin'}` : '',
      actions: (
        <div className="flex items-center gap-2">
          <Button variant="ghost" icon="arrow_back" onClick={() => navigate('/campaigning-tool/regularization/self')}>
            All runs
          </Button>
          {awaiting && (
            <Button
              variant="primary"
              icon="task_alt"
              disabled={!isAdmin || toApprove === 0}
              title={!isAdmin ? 'Only an admin can approve — this writes to vendor records' : toApprove === 0 ? 'Nothing left to approve' : undefined}
              onClick={() => setConfirming(true)}
            >
              {isAdmin ? `Approve ${num(toApprove)} vendor${toApprove === 1 ? '' : 's'}` : 'Admin approves'}
            </Button>
          )}
          {settled && isAdmin && ackable > 0 && run.provider !== 'mock' && (
            <Button variant="primary" icon="mark_email_read" onClick={() => setAskingAck(true)} title="Email each approved vendor the verified details, with one link to confirm them">
              Request acknowledgement
            </Button>
          )}
        </div>
      ),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [run?.id, run?.name, run?.status, isAdmin, toApprove, ackable]
  );

  const toggleOpen = (id) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const toggleExcluded = (id) =>
    setExcluded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const approve = async () => {
    setApproving(true);
    try {
      const res = await selfRegularizationService.approveRun(run.id, { exclude: Array.from(excluded) });
      const c = res.data?.counts || {};
      toast(`Applied to ${num(c.approved)} vendor record${c.approved === 1 ? '' : 's'}${c.excluded ? ` · ${num(c.excluded)} excluded` : ''}`, { tone: 'success' });
      setConfirming(false);
      setExcluded(new Set());
      api.refetch();
    } catch (err) {
      toast(getErrorMessage(err) || 'Could not approve the run', { tone: 'error' });
    } finally {
      setApproving(false);
    }
  };

  const requestAck = async () => {
    setRequestingAck(true);
    try {
      const res = await selfRegularizationService.requestAcknowledgement(run.id);
      const o = res.meta || {};
      const parts = [`Acknowledgement requested from ${num(o.requested)} vendor${o.requested === 1 ? '' : 's'}`];
      if (o.no_email?.length) parts.push(`${num(o.no_email.length)} with no usable email`);
      if (o.failed?.length) parts.push(`${num(o.failed.length)} could not be mailed`);
      toast(parts.join(' · '), { tone: o.failed?.length ? 'warning' : 'success' });
      setAskingAck(false);
      api.refetch();
    } catch (err) {
      toast(getErrorMessage(err) || 'Could not request acknowledgement', { tone: 'error' });
    } finally {
      setRequestingAck(false);
    }
  };

  // The tab on screen as a workbook — Summary, Vendors, and every fact behind every vendor row.
  // "In progress" has nothing settled to export, so it hands over everything instead.
  const exportBucket = tab === 'pending' ? 'all' : tab;
  const exportRun = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const filename = await selfRegularizationService.exportRun(run.id, exportBucket);
      toast(`${filename} downloaded`, { tone: 'success', icon: 'download' });
    } catch (err) {
      toast(getErrorMessage(err) || 'Could not build the Excel', { tone: 'error', icon: 'error' });
    } finally {
      setExporting(false);
    }
  };

  if (api.loading && !run) {
    return (
      <GlassCard>
        <p className="py-10 text-center text-sm text-cos-ink-secondary">Loading run…</p>
      </GlassCard>
    );
  }
  if (api.error && !run) {
    return (
      <GlassCard>
        <div className="flex flex-col items-center gap-3 py-10 text-center">
          <Icon name="search_off" className="text-[32px] text-cos-status-coral" />
          <p className="text-sm text-cos-status-coral">{api.error}</p>
          <Button variant="secondary" icon="arrow_back" onClick={() => navigate('/campaigning-tool/regularization/self')}>All runs</Button>
        </div>
      </GlassCard>
    );
  }
  if (!run) return null;

  const progress = counts.total ? (counts.done / counts.total) * 100 : 0;
  const attentionPending = approvable.filter((v) => vendorNeedsAttention(v) && !excluded.has(v.vendor_id)).length;

  return (
    <div className="flex flex-col gap-4">
      <RegistryNotice provider={run.provider} />

      {/* header: where the run stands */}
      <GlassCard accentRail>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <StatusPill status={statusPill.pill} label={statusPill.label} />
              {(run.checks || []).map((k) => (
                <CheckChip key={k} checkKey={k} size="sm" />
              ))}
            </div>
            <p className="mt-2 text-[13px] text-cos-ink-secondary">
              {live && (
                <>
                  Checking <span className="mono font-semibold text-cos-ink">{num(counts.done)}</span> of{' '}
                  <span className="mono font-semibold text-cos-ink">{num(counts.total)}</span> vendors against the registries — no email is going out.
                </>
              )}
              {awaiting && (
                <>
                  Every vendor is checked. {isAdmin ? 'Read what came back, exclude anyone you are not sure about, then approve — that is when records change.' : 'An admin reads what came back and approves it into the records.'}
                </>
              )}
              {(run.status === 'approved' || run.status === 'partially_approved') && (
                <>
                  Approved {formatDateTime(run.approved_at)} · <span className="mono font-semibold text-cos-ink">{num(counts.approved)}</span> record{counts.approved === 1 ? '' : 's'} updated
                  {counts.excluded > 0 && <> · <span className="mono font-semibold text-cos-ink">{num(counts.excluded)}</span> excluded</>}.
                  {run.acknowledgement_requested_at ? (
                    <>
                      {' '}Vendors were asked to confirm {formatDateTime(run.acknowledgement_requested_at)}:{' '}
                      <span className="mono font-semibold text-cos-ink">{num(counts.acknowledged)}</span> acknowledged
                      {counts.disputed > 0 && (
                        <>, <span className="mono font-semibold text-cos-status-coral">{num(counts.disputed)}</span> disputed</>
                      )}
                      {counts.ack_requested > 0 && (
                        <>, <span className="mono font-semibold text-cos-ink">{num(counts.ack_requested)}</span> still to answer</>
                      )}.
                    </>
                  ) : isAdmin && ackable > 0 ? (
                    <> Next, ask the approved vendors to confirm what was written — the only email this flow sends.</>
                  ) : null}
                </>
              )}
              {run.status === 'failed' && <>The registries did not answer for any vendor. Nothing was changed — run it again later.</>}
            </p>
          </div>
          <div className="text-right">
            <div className="text-2xs font-semibold uppercase tracking-wide text-cos-ink-muted">Cost</div>
            <div className="mono text-[22px] font-bold leading-none text-cos-ink">{formatInr(run.cost_actual_inr)}</div>
            <div className="mono mt-0.5 text-2xs text-cos-ink-muted">estimated {formatInr(run.cost_estimate_inr)}</div>
          </div>
        </div>

        {live && (
          <div className="mt-4">
            <ProgressBar value={progress} />
          </div>
        )}

        <div className="mt-4 grid grid-cols-2 gap-2.5 sm:grid-cols-3 xl:grid-cols-6">
          <Stat label="Vendors" value={num(counts.total)} />
          <Stat label="Verified" value={num(counts.verified)} tone="teal" hint="A registry answered for them" />
          <Stat label="Need attention" value={num(counts.attention)} tone={counts.attention ? 'coral' : 'ink'} hint="A fact a person should look at, or a number the registry does not know" />
          <Stat label="Not found" value={num(counts.not_found)} tone={counts.not_found ? 'amber' : 'ink'} hint="The registry has no such GSTIN, PAN, account or Udyam number" />
          <Stat label="Skipped" value={num(counts.skipped)} hint="Nothing on record to check — not charged" />
          <Stat label="Failed" value={num(counts.failed)} tone={counts.failed ? 'coral' : 'ink'} hint="The registry did not answer — run again later" />
        </div>

        {/* What a run cannot fix: a number the registry does not know, or nothing on record to check. That is the vendor's to supply. */}
        {!live && isAdmin && (() => {
          const toAsk = (run.vendors || []).filter((v) => v.checks.some((c) => c.status === 'not_found' || c.status === 'skipped'));
          if (toAsk.length === 0) return null;
          const asks = [...new Set(toAsk.flatMap((v) => v.checks.filter((c) => c.status === 'not_found' || c.status === 'skipped').map((c) => c.key)))];
          const href = `/campaigning-tool/regularization/send?ask=${asks.join(',')}&vendors=${toAsk.map((v) => v.vendor_id).join(',')}`;
          return (
            <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-cos-status-amber/30 bg-cos-status-amber-bg/50 px-4 py-3">
              <p className="text-[13px] text-cos-ink">
                <span className="mono font-semibold">{num(toAsk.length)}</span> vendor{toAsk.length === 1 ? '' : 's'} the registries could not settle — a number they do not know, or nothing on record to check.
                <span className="text-cos-ink-secondary"> Only the vendor can fix that.</span>
              </p>
              <Button variant="secondary" size="sm" icon="forward_to_inbox" onClick={() => navigate(href)}>
                Ask them through the vendor
              </Button>
            </div>
          );
        })()}
      </GlassCard>

      {/* the vendors */}
      <GlassCard padded={false}>
        <div id="run-vendors" className="flex flex-wrap items-center justify-between gap-3 scroll-mt-4 px-5 pt-5">
          <div>
            <h3 className="text-[15px] font-bold tracking-tight text-cos-ink">What the registries said</h3>
            <p className="mt-0.5 text-[12px] text-cos-ink-secondary">Open a vendor to see each field: what we hold, what the registry holds, and what approval would do.</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Tabs
              size="sm"
              value={tab}
              onChange={(next) => {
                setTab(next);
                setPage(1);
              }}
              items={[
                { key: 'all', label: 'All', count: buckets.all.length },
                { key: 'attention', label: 'Needs attention', count: buckets.attention.length },
                { key: 'clean', label: 'Clean', count: buckets.clean.length },
                { key: 'other', label: 'Skipped / failed', count: buckets.other.length },
                ...(buckets.pending.length ? [{ key: 'pending', label: 'In progress', count: buckets.pending.length }] : []),
              ]}
            />
            <Button
              variant="secondary"
              size="sm"
              icon={exporting ? 'hourglass_top' : 'download'}
              disabled={exporting || buckets.all.length === 0}
              onClick={exportRun}
              title={buckets.all.length === 0 ? 'Nothing to download yet' : 'Download this view as Excel — every vendor here, with the facts behind each check'}
            >
              {exporting ? 'Preparing…' : 'Download Excel'}
            </Button>
          </div>
        </div>

        <div className="flex flex-col gap-2 px-5 pb-5 pt-4">
          {visible.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-12 text-center text-cos-ink-muted">
              <Icon name="inbox" className="text-[28px]" />
              <span className="text-sm">Nothing in this view.</span>
            </div>
          ) : (
            pageRows.map((v) => (
              <VendorRow
                key={v.vendor_id}
                vendor={v}
                open={open.has(v.vendor_id)}
                onToggle={() => toggleOpen(v.vendor_id)}
                canDecide={canDecide}
                excluded={excluded.has(v.vendor_id)}
                onExclude={() => toggleExcluded(v.vendor_id)}
              />
            ))
          )}
          {visible.length > VENDORS_PER_PAGE && (
            <Pagination
              className="mt-2 border-t border-cos-line-subtle pt-3"
              page={safePage}
              pageSize={VENDORS_PER_PAGE}
              total={visible.length}
              onPageChange={(next) => {
                setPage(next);
                document.getElementById('run-vendors')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }}
              noun="vendors"
            />
          )}
        </div>
      </GlassCard>

      <Modal open={confirming} onClose={() => !approving && setConfirming(false)} size="md" labelledBy="approve-title">
        <div className="p-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3 id="approve-title" className="text-[17px] font-bold tracking-tight text-cos-ink">Approve this run?</h3>
              <p className="mt-1 text-[13px] text-cos-ink-secondary">This is the moment vendor records change.</p>
            </div>
            <ModalClose onClose={() => !approving && setConfirming(false)} />
          </div>
          <div className="mt-4 flex flex-col gap-2 text-[13px] text-cos-ink">
            <p className="flex items-start gap-2">
              <Icon name="task_alt" className="mt-px shrink-0 text-[18px] text-cos-teal-deep" weight={500} />
              <span>Registry values marked <span className="font-semibold">New</span> and <span className="font-semibold">Will update</span> are written into <span className="mono font-semibold">{num(toApprove)}</span> vendor record{toApprove === 1 ? '' : 's'}.</span>
            </p>
            {excluded.size > 0 && (
              <p className="flex items-start gap-2">
                <Icon name="block" className="mt-px shrink-0 text-[18px] text-cos-ink-muted" />
                <span><span className="mono font-semibold">{num(excluded.size)}</span> excluded vendor{excluded.size === 1 ? '' : 's'} keep{excluded.size === 1 ? 's' : ''} the results on this run; nothing is written for them.</span>
              </p>
            )}
            {attentionPending > 0 && (
              <p className="flex items-start gap-2 rounded-xl bg-cos-status-coral-bg px-3 py-2 text-cos-status-coral">
                <Icon name="priority_high" className="mt-px shrink-0 text-[18px]" weight={600} />
                <span>
                  <span className="mono font-semibold">{num(attentionPending)}</span> of these need attention. Facts marked <span className="font-semibold">Check</span> are never written automatically — their other facts are.
                </span>
              </p>
            )}
          </div>
          <div className="mt-6 flex items-center justify-end gap-2">
            <Button variant="ghost" onClick={() => setConfirming(false)} disabled={approving}>Cancel</Button>
            <Button variant="primary" icon={approving ? 'hourglass_top' : 'task_alt'} onClick={approve} disabled={approving || toApprove === 0}>
              {approving ? 'Applying…' : `Approve ${num(toApprove)}`}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal open={askingAck} onClose={() => !requestingAck && setAskingAck(false)} size="md" labelledBy="ack-title">
        <div className="p-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3 id="ack-title" className="text-[17px] font-bold tracking-tight text-cos-ink">Ask the vendors to confirm?</h3>
              <p className="mt-1 text-[13px] text-cos-ink-secondary">The only email self-regularization sends, and only after approval.</p>
            </div>
            <ModalClose onClose={() => !requestingAck && setAskingAck(false)} />
          </div>
          <div className="mt-4 flex flex-col gap-2 text-[13px] text-cos-ink">
            <p className="flex items-start gap-2">
              <Icon name="mark_email_read" className="mt-px shrink-0 text-[18px] text-cos-teal-deep" weight={500} />
              <span>
                <span className="mono font-semibold">{num(ackable)}</span> approved vendor{ackable === 1 ? '' : 's'} get{ackable === 1 ? 's' : ''} one email listing the details we
                verified and wrote, with a link to confirm them. Nothing to fill in.
              </span>
            </p>
            <p className="flex items-start gap-2">
              <Icon name="lock" className="mt-px shrink-0 text-[18px] text-cos-ink-muted" />
              <span>Nothing changes on their records. Their answers show up here as they arrive.</span>
            </p>
          </div>
          <div className="mt-6 flex items-center justify-end gap-2">
            <Button variant="ghost" onClick={() => setAskingAck(false)} disabled={requestingAck}>Cancel</Button>
            <Button variant="primary" icon={requestingAck ? 'hourglass_top' : 'send'} onClick={requestAck} disabled={requestingAck || ackable === 0}>
              {requestingAck ? 'Sending…' : `Send to ${num(ackable)}`}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default SelfRegularizationRun;
